/**
 * iter301 — My Business (Seller Business Notebook).
 *
 * Mounted at `/account/business`. Strictly additive — no existing page,
 * route, or API was modified. Bilingual (AR/EN), RTL-aware, mobile-first.
 *
 * Four tabs:
 *   • Today    — today summary card (sales, expenses, net, leads, active, low-stock)
 *   • Sales    — sales ledger + "Record Sale" modal
 *   • Inventory — product-only stock controls + service-ad list
 *   • Reports  — Today/Week/Month sales + top viewed + best category + low-stock
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasInAppHistory } from '../hooks/useSmartBack';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, RefreshCw, TrendingUp, ShoppingBag, Boxes,
  BarChart3, Loader2, Plus, Trash2, X, MessageSquare, AlertTriangle,
  CheckCircle2, Package, Wrench, ClipboardList, DollarSign,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getApiBase } from '../api/httpClient';
import './MyBusiness.css';

// iter344 — Bankak (and any other 3rd-party payment app) removed from
// user-facing options. Kept as a *display-only* legacy label so
// previously-recorded sales still render meaningfully in the Sales
// tab. Backend still accepts "bankak" for backward compatibility but
// the UI never lets a new sale pick it.
const PAYMENT_METHODS = [
  { id: 'cash',          en: 'Cash',          ar: 'نقداً' },
  { id: 'bank_transfer', en: 'Bank Transfer', ar: 'تحويل بنكي' },
  { id: 'other',         en: 'Other',         ar: 'أخرى' },
];

const PAYMENT_METHODS_DISPLAY = [
  ...PAYMENT_METHODS,
  // Legacy — only used for rendering old sales.
  { id: 'bankak',        en: 'Bankak',        ar: 'بنكك' },
];

const TABS = [
  { id: 'today',     Icon: TrendingUp, en: 'Today',     ar: 'اليوم' },
  { id: 'sales',     Icon: ShoppingBag, en: 'Sales',    ar: 'المبيعات' },
  { id: 'inventory', Icon: Boxes,      en: 'Inventory', ar: 'المخزون' },
  { id: 'reports',   Icon: BarChart3,  en: 'Reports',   ar: 'التقارير' },
];

function fmtMoney(amount, currency = 'SDG', isArabic = false) {
  const n = Math.round(Number(amount || 0));
  const formatted = n.toLocaleString(isArabic ? 'ar-EG' : 'en-US');
  return `${formatted} ${currency}`;
}

// ════════════════════════════════════════════════════════════════
// TODAY tab
// ════════════════════════════════════════════════════════════════

function TodayTab({ token, isArabic, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!token) { setErr('AUTH'); setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${getApiBase()}/api/business/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErr(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) return <div className="biz-loading"><Loader2 className="animate-spin" size={18} /> {isArabic ? 'جارٍ التحميل…' : 'Loading…'}</div>;
  if (err) return <div className="biz-error">{isArabic ? 'تعذر تحميل البيانات' : 'Failed to load'}: {err}</div>;
  if (!data) return null;

  const tiles = [
    { id: 'today_sales',    en: "Today's Sales",   ar: 'مبيعات اليوم',  value: fmtMoney(data.today_sales, data.currency, isArabic),    color: 'green' },
    { id: 'today_expenses', en: "Today's Expenses",ar: 'مصاريف اليوم',  value: fmtMoney(data.today_expenses, data.currency, isArabic), color: 'red' },
    { id: 'net_today',      en: 'Net Today',       ar: 'الصافي',         value: fmtMoney(data.net_today, data.currency, isArabic),     color: data.net_today >= 0 ? 'blue' : 'red' },
    { id: 'leads_today',    en: 'Leads / Messages',ar: 'رسائل اليوم',    value: data.leads_today,                                       color: 'purple' },
    { id: 'active_ads',     en: 'Active Ads',      ar: 'إعلانات نشطة',   value: data.active_ads,                                        color: 'gray' },
    { id: 'low_stock_ads',  en: 'Low Stock',       ar: 'منخفض المخزون',  value: data.low_stock_ads,                                     color: data.low_stock_ads > 0 ? 'yellow' : 'gray' },
  ];

  return (
    <div className="biz-today" data-testid="biz-today">
      <div className="biz-today-grid">
        {tiles.map(t => (
          <div key={t.id} className={`biz-tile biz-tile-${t.color}`} data-testid={`biz-tile-${t.id}`}>
            <div className="biz-tile-label">{isArabic ? t.ar : t.en}</div>
            <div className="biz-tile-value">{t.value}</div>
          </div>
        ))}
      </div>
      <div className="biz-today-hint">
        {isArabic
          ? 'دفترك المحاسبي خاص بك ولا يتم تسجيل أي عملية تلقائياً. اضغط "تسجيل مبيعة" في تبويب المبيعات بعد كل عملية حقيقية.'
          : 'Your private business notebook. Nothing is logged automatically. Tap "Record Sale" after each real transaction to keep your numbers accurate.'}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SALES tab
// ════════════════════════════════════════════════════════════════

function RecordSaleModal({ open, onClose, onSaved, token, isArabic, myAds }) {
  const [form, setForm] = useState({
    ad_id: '', title: '', amount: '', payment_method: 'cash',
    buyer_name: '', notes: '', sale_date: '',
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({ ad_id: '', title: '', amount: '', payment_method: 'cash',
        buyer_name: '', notes: '', sale_date: '' });
    }
  }, [open]);
  if (!open) return null;

  const canSubmit = !saving && form.title.trim().length > 0 && Number(form.amount) >= 0;

  const submit = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${getApiBase()}/api/business/sales`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ad_id: form.ad_id || null,
          title: form.title.trim(),
          amount: Number(form.amount),
          payment_method: form.payment_method,
          buyer_name: form.buyer_name.trim() || null,
          notes: form.notes.trim() || null,
          sale_date: form.sale_date ? new Date(form.sale_date).toISOString() : null,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(j.detail || (isArabic ? 'فشل الحفظ' : 'Save failed')); return; }
      toast.success(isArabic ? 'تم تسجيل المبيعة' : 'Sale recorded');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(isArabic ? 'خطأ في الشبكة' : 'Network error');
    } finally { setSaving(false); }
  };

  return (
    <div className="biz-modal-backdrop" role="dialog" aria-modal="true" data-testid="biz-record-sale-modal">
      <div className="biz-modal">
        <div className="biz-modal-header">
          <h3>{isArabic ? 'تسجيل مبيعة' : 'Record Sale'}</h3>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="biz-record-sale-close"><X size={18} /></button>
        </div>
        <div className="biz-modal-body">
          <label>{isArabic ? 'الإعلان (اختياري)' : 'Linked Ad (optional)'}</label>
          <select
            value={form.ad_id}
            onChange={(e) => {
              const sel = myAds.find(a => a.ad_id === e.target.value);
              setForm(f => ({ ...f, ad_id: e.target.value, title: f.title || (sel?.title || '') }));
            }}
            data-testid="biz-record-sale-ad"
          >
            <option value="">{isArabic ? '— لا يوجد —' : '— None —'}</option>
            {myAds.map(a => <option key={a.ad_id} value={a.ad_id}>{a.title}</option>)}
          </select>

          <label>{isArabic ? 'العنوان' : 'Sale Title'} <span className="biz-req">*</span></label>
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            maxLength={200} required data-testid="biz-record-sale-title" />

          <label>{isArabic ? 'المبلغ (SDG)' : 'Amount (SDG)'} <span className="biz-req">*</span></label>
          <input type="number" min={0} step={1} value={form.amount}
            onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required
            data-testid="biz-record-sale-amount" />

          <label>{isArabic ? 'طريقة الدفع' : 'Payment Method'}</label>
          <div className="biz-pm-grid">
            {PAYMENT_METHODS.map(pm => (
              <button key={pm.id} type="button"
                className={`biz-pm-chip ${form.payment_method === pm.id ? 'on' : ''}`}
                onClick={() => setForm(f => ({ ...f, payment_method: pm.id }))}
                data-testid={`biz-pm-${pm.id}`}
              >
                {isArabic ? pm.ar : pm.en}
              </button>
            ))}
          </div>

          <label>{isArabic ? 'اسم المشتري (اختياري)' : 'Buyer Name (optional)'}</label>
          <input value={form.buyer_name} onChange={e => setForm(f => ({ ...f, buyer_name: e.target.value }))}
            maxLength={200} data-testid="biz-record-sale-buyer" />

          <label>{isArabic ? 'ملاحظات (اختياري)' : 'Notes (optional)'}</label>
          <textarea rows={2} value={form.notes} maxLength={1000}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            data-testid="biz-record-sale-notes" />

          <label>{isArabic ? 'التاريخ (اختياري)' : 'Date (optional)'}</label>
          <input type="datetime-local" value={form.sale_date}
            onChange={e => setForm(f => ({ ...f, sale_date: e.target.value }))}
            data-testid="biz-record-sale-date" />
        </div>
        <div className="biz-modal-footer">
          <button type="button" className="biz-btn" onClick={onClose} disabled={saving}>
            {isArabic ? 'إلغاء' : 'Cancel'}
          </button>
          <button type="button" className="biz-btn biz-btn-primary" onClick={submit} disabled={!canSubmit}
            data-testid="biz-record-sale-submit">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
            <span style={{ marginInlineStart: 4 }}>{isArabic ? 'حفظ' : 'Save'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function SalesTab({ token, isArabic, myAds, refreshKey }) {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('today');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${getApiBase()}/api/business/sales?range=${filter}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const j = await r.json();
        setSales(j.sales || []);
      } else setSales([]);
    } catch (_e) { setSales([]); }
    finally { setLoading(false); }
  }, [token, filter]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const deleteSale = async (sale_id) => {
    if (!window.confirm(isArabic ? 'حذف هذه المبيعة؟' : 'Delete this sale?')) return;
    try {
      const r = await fetch(`${getApiBase()}/api/business/sales/${sale_id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) { toast.success(isArabic ? 'تم الحذف' : 'Deleted'); load(); }
    } catch (_e) { /* ignored */ }
  };

  return (
    <div className="biz-sales" data-testid="biz-sales">
      <div className="biz-section-header">
        <div className="biz-filter-chips">
          {['today', 'week', 'month', 'all'].map(f => (
            <button key={f} type="button"
              className={`biz-pill ${filter === f ? 'on' : ''}`}
              onClick={() => setFilter(f)}
              data-testid={`biz-sales-filter-${f}`}
            >
              {isArabic
                ? ({ today: 'اليوم', week: 'الأسبوع', month: 'الشهر', all: 'الكل' }[f])
                : ({ today: 'Today', week: 'Week', month: 'Month', all: 'All' }[f])}
            </button>
          ))}
        </div>
        <button type="button" className="biz-btn biz-btn-primary" onClick={() => setShowModal(true)} data-testid="biz-record-sale-btn">
          <Plus size={14} /> <span>{isArabic ? 'تسجيل مبيعة' : 'Record Sale'}</span>
        </button>
      </div>

      {loading && <div className="biz-loading"><Loader2 className="animate-spin" size={16} /> {isArabic ? 'جارٍ التحميل…' : 'Loading…'}</div>}

      {!loading && sales.length === 0 && (
        <div className="biz-empty" data-testid="biz-sales-empty">
          <ClipboardList size={32} />
          <p>{isArabic ? 'لا توجد مبيعات مسجلة بعد' : 'No sales recorded yet'}</p>
          <p className="biz-empty-hint">
            {isArabic
              ? 'اضغط "تسجيل مبيعة" بعد كل عملية بيع حقيقية. لن نسجل أي شيء تلقائياً.'
              : 'Tap "Record Sale" after each real sale. Nothing is logged automatically.'}
          </p>
        </div>
      )}

      {!loading && sales.length > 0 && (
        <div className="biz-list">
          {sales.map(s => (
            <div key={s.sale_id} className="biz-row" data-testid={`biz-sale-row-${s.sale_id}`}>
              <div className="biz-row-main">
                <div className="biz-row-title">{s.title}</div>
                <div className="biz-row-sub">
                  {fmtMoney(s.amount, s.currency, isArabic)}
                  {' · '}
                  {PAYMENT_METHODS_DISPLAY.find(p => p.id === s.payment_method)?.[isArabic ? 'ar' : 'en'] || s.payment_method}
                  {s.buyer_name && ` · ${s.buyer_name}`}
                </div>
                {s.notes && <div className="biz-row-notes">{s.notes}</div>}
                <div className="biz-row-meta">
                  {s.sale_date && new Date(s.sale_date).toLocaleString(isArabic ? 'ar-EG' : 'en-US')}
                  {s.ad_id && ` · #${s.ad_id.slice(0, 12)}`}
                </div>
              </div>
              <button type="button" className="biz-row-delete" onClick={() => deleteSale(s.sale_id)} aria-label="delete"
                data-testid={`biz-sale-delete-${s.sale_id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <RecordSaleModal open={showModal} onClose={() => setShowModal(false)} onSaved={load}
        token={token} isArabic={isArabic} myAds={myAds} />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// INVENTORY tab
// ════════════════════════════════════════════════════════════════

function InventoryTab({ token, isArabic, myAds, refreshAds }) {
  const [busy, setBusy] = useState(null); // ad_id currently mutating

  const productAds = myAds.filter(a => {
    const lt = (a.listing_type || 'product').toLowerCase();
    return !['service', 'service_offering'].includes(lt);
  });
  const serviceAds = myAds.filter(a => {
    const lt = (a.listing_type || 'product').toLowerCase();
    return ['service', 'service_offering'].includes(lt);
  });

  const callAdjust = async (ad_id, action, opts = {}) => {
    setBusy(ad_id);
    try {
      const r = await fetch(`${getApiBase()}/api/business/inventory/${ad_id}/adjust`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ...opts }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(j.detail || 'Failed'); return; }
      // Out-of-stock prompt.
      if (j.prompt_hide_ad) {
        const hide = window.confirm(isArabic
          ? 'وصلت الكمية إلى 0. هل ترغب في إخفاء الإعلان من المتجر؟'
          : 'Stock reached 0. Hide this ad from the public Shop?');
        if (hide) {
          await fetch(`${getApiBase()}/api/business/inventory/${ad_id}/adjust`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'mark_out_of_stock', hide_ad_now: true }),
          });
          toast.success(isArabic ? 'تم إخفاء الإعلان' : 'Ad hidden');
        }
      } else {
        toast.success(isArabic ? 'تم التحديث' : 'Updated');
      }
      refreshAds && refreshAds();
    } catch (_e) { toast.error(isArabic ? 'خطأ' : 'Error'); }
    finally { setBusy(null); }
  };

  const adjustWithPrompt = (ad_id, action) => {
    const label = action === 'add_stock'
      ? (isArabic ? 'الكمية المضافة' : 'Quantity to add')
      : (isArabic ? 'الكمية' : 'Quantity');
    const raw = window.prompt(label, '1');
    if (raw === null) return;
    const qty = Math.max(0, parseInt(raw, 10) || 0);
    if (!qty) return;
    callAdjust(ad_id, action, { quantity: qty });
  };

  const setStockWithPrompt = (ad_id) => {
    const raw = window.prompt(isArabic ? 'الكمية الجديدة' : 'New stock quantity', '0');
    if (raw === null) return;
    const qty = Math.max(0, parseInt(raw, 10) || 0);
    callAdjust(ad_id, 'set_stock', { quantity: qty });
  };

  return (
    <div className="biz-inventory" data-testid="biz-inventory">
      <div className="biz-section-title"><Package size={16} /> {isArabic ? 'المنتجات' : 'Products'}</div>

      {productAds.length === 0 && (
        <div className="biz-empty"><Boxes size={28} />
          <p>{isArabic ? 'لا توجد إعلانات منتجات' : 'No product ads yet'}</p>
        </div>
      )}

      {productAds.map(a => {
        const stock = (typeof a.stock_remaining === 'number') ? a.stock_remaining : null;
        const sold = a.sold_count || 0;
        const lowThr = a.low_stock_threshold ?? 3;
        const isLow = stock !== null && stock > 0 && stock <= lowThr;
        const isOut = stock === 0;
        return (
          <div key={a.ad_id} className="biz-inv-card" data-testid={`biz-inv-card-${a.ad_id}`}>
            <div className="biz-inv-card-head">
              <div className="biz-inv-card-title">{a.title}</div>
              <div className="biz-inv-card-badges">
                {stock !== null && <span className={`biz-badge ${isOut ? 'red' : isLow ? 'yellow' : 'green'}`}>
                  {isArabic ? `المخزون: ${stock}` : `Stock: ${stock}`}
                </span>}
                <span className="biz-badge gray">{isArabic ? `المباع: ${sold}` : `Sold: ${sold}`}</span>
                {a.is_hidden && <span className="biz-badge red">{isArabic ? 'مخفي' : 'Hidden'}</span>}
              </div>
            </div>
            <div className="biz-inv-actions">
              <button type="button" className="biz-btn" disabled={busy === a.ad_id}
                onClick={() => setStockWithPrompt(a.ad_id)}
                data-testid={`biz-inv-set-${a.ad_id}`}>
                {isArabic ? 'ضبط المخزون' : 'Set Stock'}
              </button>
              <button type="button" className="biz-btn" disabled={busy === a.ad_id}
                onClick={() => adjustWithPrompt(a.ad_id, 'add_stock')}
                data-testid={`biz-inv-add-${a.ad_id}`}>
                + {isArabic ? 'إضافة' : 'Add'}
              </button>
              <button type="button" className="biz-btn" disabled={busy === a.ad_id}
                onClick={() => adjustWithPrompt(a.ad_id, 'reduce_stock')}
                data-testid={`biz-inv-reduce-${a.ad_id}`}>
                − {isArabic ? 'خصم' : 'Reduce'}
              </button>
              <button type="button" className="biz-btn biz-btn-primary" disabled={busy === a.ad_id}
                onClick={() => callAdjust(a.ad_id, 'mark_sold', { quantity: 1 })}
                data-testid={`biz-inv-sold-${a.ad_id}`}>
                <CheckCircle2 size={12} /> {isArabic ? 'مباع' : 'Sold'}
              </button>
              <button type="button" className="biz-btn biz-btn-danger" disabled={busy === a.ad_id}
                onClick={() => callAdjust(a.ad_id, 'mark_out_of_stock')}
                data-testid={`biz-inv-out-${a.ad_id}`}>
                <AlertTriangle size={12} /> {isArabic ? 'نفاد' : 'Out'}
              </button>
            </div>
          </div>
        );
      })}

      <div className="biz-section-title" style={{ marginTop: 28 }}>
        <Wrench size={16} /> {isArabic ? 'الخدمات' : 'Services'}
      </div>
      {serviceAds.length === 0 && (
        <div className="biz-empty"><Wrench size={28} /><p>{isArabic ? 'لا توجد خدمات مسجلة' : 'No service ads'}</p></div>
      )}
      {serviceAds.map(a => (
        <div key={a.ad_id} className="biz-inv-card" data-testid={`biz-svc-card-${a.ad_id}`}>
          <div className="biz-inv-card-head">
            <div className="biz-inv-card-title">{a.title}</div>
            <div className="biz-inv-card-badges">
              <span className="biz-badge gray">{isArabic ? `وظائف منجزة: ${a.sold_count || 0}` : `Completed: ${a.sold_count || 0}`}</span>
              <span className="biz-badge blue">{isArabic ? `الإيراد: ${fmtMoney(a._revenue || 0, 'SDG', isArabic)}` : `Income: ${fmtMoney(a._revenue || 0, 'SDG', isArabic)}`}</span>
            </div>
          </div>
          <div className="biz-inv-actions">
            <button type="button" className="biz-btn biz-btn-primary"
              onClick={() => callAdjust(a.ad_id, 'mark_sold', { quantity: 1 })}
              data-testid={`biz-svc-completed-${a.ad_id}`}>
              <CheckCircle2 size={12} /> {isArabic ? 'وظيفة منجزة' : 'Mark Completed'}
            </button>
          </div>
          <div className="biz-row-notes">
            {isArabic
              ? 'لا يوجد مخزون للخدمات. سجّل كل وظيفة منجزة لمتابعة دخلك.'
              : 'Services have no stock. Tap "Mark Completed" after each finished job.'}
          </div>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// REPORTS tab — iter344 range-filtered
// ════════════════════════════════════════════════════════════════

// iter344 — Compute local-timezone window boundaries in the browser
// and send them as ISO strings to the backend. This avoids UTC
// date-shift bugs where a sale recorded "4 June local time" could
// end up categorized as 3 June (or 5 June) in UTC.
function computeRange(kind, customFrom, customTo) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const nextDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);

  if (kind === 'today') return { from: start, to: nextDay(start) };
  if (kind === 'week') {
    // Monday 00:00 (ISO week). getDay(): Sun=0..Sat=6 → Mon=1.
    const dow = start.getDay();
    const daysBack = (dow === 0) ? 6 : dow - 1;
    const from = new Date(start.getFullYear(), start.getMonth(), start.getDate() - daysBack, 0, 0, 0, 0);
    const to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 7, 0, 0, 0, 0);
    return { from, to };
  }
  if (kind === 'month') {
    const from = new Date(start.getFullYear(), start.getMonth(), 1, 0, 0, 0, 0);
    const to = new Date(start.getFullYear(), start.getMonth() + 1, 1, 0, 0, 0, 0);
    return { from, to };
  }
  if (kind === 'quarter') {
    const qMonth = Math.floor(start.getMonth() / 3) * 3;
    const from = new Date(start.getFullYear(), qMonth, 1, 0, 0, 0, 0);
    const to = new Date(start.getFullYear(), qMonth + 3, 1, 0, 0, 0, 0);
    return { from, to };
  }
  if (kind === 'year') {
    const from = new Date(start.getFullYear(), 0, 1, 0, 0, 0, 0);
    const to = new Date(start.getFullYear() + 1, 0, 1, 0, 0, 0, 0);
    return { from, to };
  }
  if (kind === 'all') return { from: null, to: null };
  if (kind === 'custom') {
    const parseYmd = (s) => {
      if (!s) return null;
      const [y, m, d] = s.split('-').map(Number);
      if (!y || !m || !d) return null;
      return new Date(y, m - 1, d, 0, 0, 0, 0);
    };
    const from = parseYmd(customFrom);
    const toDay = parseYmd(customTo);
    const to = toDay ? nextDay(toDay) : null;
    return { from, to };
  }
  // Fallback: month
  const from = new Date(start.getFullYear(), start.getMonth(), 1, 0, 0, 0, 0);
  const to = new Date(start.getFullYear(), start.getMonth() + 1, 1, 0, 0, 0, 0);
  return { from, to };
}

const REPORT_RANGES = [
  { id: 'today',   en: 'Today',        ar: 'اليوم' },
  { id: 'week',    en: 'This Week',    ar: 'هذا الأسبوع' },
  { id: 'month',   en: 'This Month',   ar: 'هذا الشهر' },
  { id: 'quarter', en: 'This Quarter', ar: 'هذا الربع' },
  { id: 'year',    en: 'This Year',    ar: 'هذا العام' },
  { id: 'all',     en: 'All Time',     ar: 'كل الوقت' },
  { id: 'custom',  en: 'Custom',       ar: 'مخصص' },
];

function ReportsTab({ token, isArabic, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeKind, setRangeKind] = useState('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const load = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    const { from, to } = computeRange(rangeKind, customFrom, customTo);
    if (rangeKind === 'custom' && (!from || !to)) {
      // Wait for both custom endpoints before hitting the backend.
      setLoading(false);
      return;
    }
    const qs = new URLSearchParams({ range: rangeKind });
    if (from) qs.set('date_from', from.toISOString());
    if (to) qs.set('date_to', to.toISOString());
    try {
      const r = await fetch(`${getApiBase()}/api/business/reports?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
    } catch (_e) { /* */ }
    finally { setLoading(false); }
  }, [token, rangeKind, customFrom, customTo]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const total = Number(data?.total_sales || 0);
  const count = Number(data?.sales_count || 0);
  const avg = Number(data?.avg_sale_value || 0);

  return (
    <div className="biz-reports" data-testid="biz-reports">
      {/* Range picker */}
      <div
        className="biz-rep-range"
        data-testid="biz-rep-range"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}
      >
        {REPORT_RANGES.map(r => (
          <button
            key={r.id}
            type="button"
            className={`biz-pm-chip ${rangeKind === r.id ? 'on' : ''}`}
            onClick={() => setRangeKind(r.id)}
            data-testid={`biz-rep-range-${r.id}`}
            style={{ fontSize: 12, padding: '6px 10px' }}
          >
            {isArabic ? r.ar : r.en}
          </button>
        ))}
      </div>

      {rangeKind === 'custom' && (
        <div
          className="biz-rep-custom"
          data-testid="biz-rep-custom"
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}
        >
          <label style={{ flex: '1 1 140px', fontSize: 12 }}>
            {isArabic ? 'من تاريخ' : 'From'}
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              data-testid="biz-rep-custom-from"
              style={{ width: '100%', marginTop: 4 }}
            />
          </label>
          <label style={{ flex: '1 1 140px', fontSize: 12 }}>
            {isArabic ? 'إلى تاريخ' : 'To'}
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              data-testid="biz-rep-custom-to"
              style={{ width: '100%', marginTop: 4 }}
            />
          </label>
        </div>
      )}

      {loading && (
        <div className="biz-loading">
          <Loader2 className="animate-spin" size={16} /> {isArabic ? 'جارٍ التحميل…' : 'Loading…'}
        </div>
      )}

      {!loading && data && (
        <>
          <div className="biz-rep-grid">
            <div className="biz-tile biz-tile-blue" data-testid="biz-rep-total-sales">
              <div className="biz-tile-label">{isArabic ? 'إجمالي المبيعات' : 'Total Sales'}</div>
              <div className="biz-tile-value">{fmtMoney(total, data.currency, isArabic)}</div>
            </div>
            <div className="biz-tile biz-tile-green" data-testid="biz-rep-sales-count">
              <div className="biz-tile-label">{isArabic ? 'عدد المبيعات' : '# of Sales'}</div>
              <div className="biz-tile-value">{count}</div>
            </div>
            <div className="biz-tile biz-tile-purple" data-testid="biz-rep-avg-sale">
              <div className="biz-tile-label">{isArabic ? 'متوسط قيمة المبيعة' : 'Avg Sale Value'}</div>
              <div className="biz-tile-value">{fmtMoney(avg, data.currency, isArabic)}</div>
            </div>
          </div>

          <div className="biz-rep-section">
            <h4>{isArabic ? 'أكثر إعلان مشاهدة' : 'Top viewed ad'}</h4>
            {data.top_viewed_ad
              ? <div className="biz-rep-row">{data.top_viewed_ad.title} — {data.top_viewed_ad.view_count || 0} {isArabic ? 'مشاهدة' : 'views'}</div>
              : <div className="biz-empty-mini">{isArabic ? 'لا توجد بيانات بعد' : 'No data yet'}</div>}
          </div>

          <div className="biz-rep-section">
            <h4>{isArabic ? 'أفضل تصنيف' : 'Best category'}</h4>
            {data.best_category && data.best_category.category
              ? <div className="biz-rep-row">
                  {data.best_category.category} — {fmtMoney(data.best_category.sum_amount, data.currency, isArabic)} · {data.best_category.count} {isArabic ? 'مبيعات' : 'sales'}
                </div>
              : <div className="biz-empty-mini">{isArabic ? 'لا توجد بيانات بعد' : 'No data yet'}</div>}
          </div>

          <div className="biz-rep-section" data-testid="biz-low-stock-card">
            <h4>{isArabic ? 'منخفض المخزون' : 'Low-stock items'}</h4>
            {/* iter343 — Three-state Low-stock card. Uses inventory_items_count
                (product ads with stock_remaining set — i.e., inventory
                tracking is turned on) instead of inferring from live ads
                count so we never claim "well-stocked" when there is
                literally no inventory to be well-stocked. */}
            {(() => {
              const invCount = Number(data.inventory_items_count || 0);
              const lowItems = Array.isArray(data.low_stock_items) ? data.low_stock_items : [];
              if (lowItems.length > 0) {
                return (
                  <>
                    <div
                      className="biz-rep-row biz-rep-lowstock-count"
                      data-testid="biz-low-stock-count"
                    >
                      {isArabic
                        ? `${lowItems.length} ${lowItems.length === 1 ? 'منتج منخفض المخزون' : 'منتجات منخفضة المخزون'}`
                        : `${lowItems.length} low-stock ${lowItems.length === 1 ? 'item' : 'items'}`}
                    </div>
                    <ul className="biz-rep-list" data-testid="biz-low-stock-list">
                      {lowItems.map(it => (
                        <li key={it.ad_id}>{it.title} — {it.stock_remaining} {isArabic ? 'وحدة' : 'left'}</li>
                      ))}
                    </ul>
                  </>
                );
              }
              if (invCount === 0) {
                return (
                  <div className="biz-empty-mini" data-testid="biz-low-stock-empty">
                    <div style={{ fontWeight: 600, color: '#334155' }}>
                      {isArabic ? 'لا توجد عناصر مخزون بعد' : 'No inventory items yet'}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                      {isArabic ? 'أضف منتجاتك لتتبع مستوى المخزون.' : 'Add products to track stock levels.'}
                    </div>
                  </div>
                );
              }
              return (
                <div className="biz-empty-mini" data-testid="biz-low-stock-ok">
                  {isArabic ? 'كل المنتجات متوفرة بشكل جيد' : 'All products are well-stocked'}
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// Root
// ════════════════════════════════════════════════════════════════

export default function MyBusiness() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const isArabic = lang === 'ar';
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('today');
  const [myAds, setMyAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadAds = useCallback(async () => {
    if (!token) return;
    setAdsLoading(true);
    try {
      const r = await fetch(`${getApiBase()}/api/ads/my-ads?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const j = await r.json();
        setMyAds(j.ads || []);
      } else {
        console.warn('MyBusiness loadAds failed:', r.status);
      }
    } catch (_e) { /* */ }
    finally { setAdsLoading(false); }
  }, [token]);

  useEffect(() => { loadAds(); }, [loadAds]);

  // iter373 — Refresh button re-fetches everything (ads + active tab data)
  // read-only, with duplicate-tap protection and a spinning state.
  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await loadAds();
      setRefreshKey(k => k + 1);
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, loadAds]);

  const BackIcon = isArabic ? ArrowRight : ArrowLeft;

  return (
    <div className="biz-page" dir={isArabic ? 'rtl' : 'ltr'} data-testid="my-business-page">
      <div className="biz-topbar">
        <button type="button" className="biz-back-btn" onClick={() => { if (hasInAppHistory()) navigate(-1); else navigate('/account', { replace: true }); }} aria-label="back"
          data-testid="biz-back-btn">
          <BackIcon size={18} />
        </button>
        <h1 className="biz-title">{isArabic ? 'دفتر الأعمال' : 'My Business'}</h1>
        <button type="button" className="biz-refresh-btn" onClick={handleRefresh} aria-label="refresh"
          disabled={refreshing} data-testid="biz-refresh-btn">
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="biz-tabs" role="tablist">
        {TABS.map(t => (
          <button key={t.id} role="tab"
            className={`biz-tab ${activeTab === t.id ? 'on' : ''}`}
            onClick={() => setActiveTab(t.id)}
            data-testid={`biz-tab-${t.id}`}
            aria-selected={activeTab === t.id}
          >
            <t.Icon size={14} />
            <span>{isArabic ? t.ar : t.en}</span>
          </button>
        ))}
      </div>

      <div className="biz-tab-body">
        {activeTab === 'today' && <TodayTab token={token} isArabic={isArabic} refreshKey={refreshKey} />}
        {activeTab === 'sales' && <SalesTab token={token} isArabic={isArabic} myAds={myAds} refreshKey={refreshKey} />}
        {activeTab === 'inventory' && (
          adsLoading
            ? <div className="biz-loading"><Loader2 className="animate-spin" size={16} /> {isArabic ? 'جارٍ التحميل…' : 'Loading…'}</div>
            : <InventoryTab token={token} isArabic={isArabic} myAds={myAds} refreshAds={loadAds} />
        )}
        {activeTab === 'reports' && <ReportsTab token={token} isArabic={isArabic} refreshKey={refreshKey} />}
      </div>

      {/* keep unused icons referenced to avoid linter warnings */}
      <span style={{ display: 'none' }}><MessageSquare size={1} /><DollarSign size={1} /></span>
    </div>
  );
}
